import { useState, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { parseISO, format } from 'date-fns'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import styled from '@emotion/styled'

import {
  useArticle,
  useArticleById,
  useLoginData,
  useArticleClassifications,
} from 'utils/api/hooks'
import { setArticleViewed } from 'utils/api'
import { getVenderWelcomeInfo } from 'utils/helpers/vender-welcome-info'
import { selectWebview } from 'store/webview/webviewSlice'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import { useGtmArticlePageView } from 'utils/hooks/analytics'
import { useShowScrollToTopButton } from 'utils/hooks'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import ContentWrapper from 'components/common/ContentWrapper'
import Link from 'components/common/A'
import NowrapDiv from 'components/common/NowrapDiv'
import { EyeSVG, ClockSVG, PenSVG } from 'components/common/SVGElements'
import TagButton, { OneLineGrid } from 'components/common/TagButton'
import HTMLContent from 'components/common/HTMLContent'
import VenderLink from 'components/common/VenderLink'
import Controls from './Controls'
import ShareButton from 'components/common/ShareButton'
import VenderInfo from './VenderInfo'
import GuessLikesList from 'components/common/GuessLikesList'
import BannerCarousel from 'components/common/BannerCarouselForArticle'
import LatestArticle from './LatestArticle'
import { ArticleMainImgLogo } from 'components/common/SVGElements'
import ScrollableWrapper from 'components/common/ScrollableWrapper'

const DynamicIdleModal = dynamic(() => import('./IdleModal'))
const DynamicFooter = dynamic(() => import('components/common/Footer'), {
  ssr: false,
})

const CustomLink = styled(Link)`
  ${({ theme }) => `
    transition-delay: 0s;
    transition-duration: .2s;
    transition-property: color;
    transition-timing-function: ease;

    &:hover {
      color: ${theme.palette.primary.main};
    }
  `}
`

const Img = styled.img`
  width: 100%;
  object-fit: cover;
`

const MainImgBox = styled.div`
  ${({ theme }) => `
  padding-bottom: 50%;
  ${theme.breakpoints.up('lg')} {
    padding-bottom: 37.5%;
  }
  `}
  width: 100%;
  position: relative;
  margin-bottom: 32px;
  border-radius: 10px;
  overflow: hidden;
  -webkit-mask-image: -webkit-radial-gradient(
    white,
    black
  ); // 處理 safari img & mask border-radius 於 overflow-hidden 失效問題
`

const MainImg = styled(Img)`
  position: absolute;
  height: 100%;
  z-index: 1;
`

const MainImgMask = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 40px;
  z-index: 2;
  background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.5));
  display: flex;
  align-items: center;
  padding: 0 20px;
`

const VenderName = styled(Typography)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  white-space: normal;
  color: rgba(256, 256, 256, 0.8);
  margin-left: 10px;
`

const Scrollable = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  scroll-behavior: smooth;
`
const Absolute = styled.div`
  position: absolute;
  width: calc((100% - 1104px) / 2);
  height: calc(100% - 50px);
  top: 25px;
  left: 0;
  z-index: 3;
`
const Sticky = styled.div`
  position: sticky;
  top: 0;
`
const ArticleWrapper = styled(ContentWrapper)`
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
`

const ArticleContentWrapper = styled.div`
  max-width: 1104px;
  width: 100vw;
  padding: 0 16px;
  display: flex;
`
const SmallVenderIconImage = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
`

const Line = styled.span`
  ${({ theme }) => `
    display: inline-block;
    width: 100%;
    height: 1px;
    background-color: ${theme.palette.background.line};
    margin-top: 48px;
  `}
`

const BottomSpacing = styled.div`
  ${({ theme }) => `
    margin-bottom: 98px;
    ${theme.breakpoints.up('lg')} {
      margin-bottom: 198px;
    }
    ${theme.breakpoints.up('xl')} {
      margin-bottom: 222px;
    }
  `}
`

const MainImageMaskWithLogo = ({ venderName, venderVatNo }) => {
  return (
    <>
      {venderVatNo && venderVatNo !== '22354940' ? (
        <MainImgMask>
          <ArticleMainImgLogo />
          <VenderName>{`。` + venderName}</VenderName>
        </MainImgMask>
      ) : (
        <MainImgMask>
          <ArticleMainImgLogo />
        </MainImgMask>
      )}
    </>
  )
}

const IconInfos = ({ article }) => {
  const theme = useTheme()
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <>
      <Box mr={4} display="flex" flexWrap="nowrap" alignItems="center">
        <EyeSVG
          width={isMedium ? '16px' : '20px'}
          height={isMedium ? '16px' : '20px'}
          $color="text.disabled"
        />
        <Typography
          ml={1.5}
          color="text.disabled"
          sx={{
            typography: { xs: 'bodyXs', md: 'bodySmall' },
          }}
        >
          {article?.publicViews ?? 0}
        </Typography>
      </Box>
      <Box mr={4} display="flex" flexWrap="nowrap" alignItems="center">
        <ClockSVG
          width={isMedium ? '16px' : '20px'}
          height={isMedium ? '16px' : '20px'}
          $color="text.disabled"
        />
        <Typography
          ml={1.5}
          color="text.disabled"
          sx={{
            typography: { xs: 'bodyXs', md: 'bodySmall' },
          }}
        >
          {!!article?.displayPublishDate &&
            format(parseISO(article.displayPublishDate), 'yyyy/MM/dd')}
        </Typography>
      </Box>
      <Box display="flex" flexWrap="nowrap" alignItems="center">
        {article?.vender?.introductionInfo?.isVisible ? (
          <VenderLink
            vender={article?.vender}
            width={isMedium ? '16px' : '20px'}
            height={isMedium ? '16px' : '20px'}
          />
        ) : (
          <>
            <PenSVG
              width={isMedium ? '16px' : '20px'}
              height={isMedium ? '16px' : '20px'}
              $color="text.disabled"
            />
            <Typography
              ml={1.5}
              color="text.disabled"
              sx={{
                typography: { xs: 'bodyXs', md: 'bodySmall' },
              }}
            >
              {article?.byline}
            </Typography>
          </>
        )}
      </Box>
    </>
  )
}

const ArticleClassifications = ({ articleClassificationItem }) => {
  const theme = useTheme()
  const [inHover, setInHover] = useState(false)

  return (
    <Grid item xs={6}>
      <Link
        href={`/article-classification/${articleClassificationItem?.urlId}`}
      >
        <Box
          display="flex"
          alignItems="center"
          mb={4}
          onMouseEnter={() => setInHover(true)}
          onMouseLeave={() => setInHover(false)}
        >
          <Typography
            color={inHover ? theme.palette.primary.main : 'text.minus2'}
          >
            {articleClassificationItem.name}
          </Typography>
          <Typography
            color={inHover ? theme.palette.primary.minus2 : 'textSecondary'}
            ml={1}
          >
            {`(${articleClassificationItem.articles?.length})`}
          </Typography>
        </Box>
      </Link>
    </Grid>
  )
}

const useIdleModal = (isMobile, scrollableRef) => {
  const [idleModal, setIdleModal] = useState({ open: false })
  const closeIdleModal = () => setIdleModal({ open: false })

  useEffect(() => {
    if (isMobile || idleModal?.open) {
      return
    }

    let timer = null
    const resetTimer = () => {
      if (timer) {
        clearTimeout(timer)
      }
      timer = setTimeout(() => setIdleModal({ open: true }), 60 * 1000)
    }

    resetTimer()

    document.addEventListener('mousemove', resetTimer)
    document.addEventListener('click', resetTimer)
    document.addEventListener('keypress', resetTimer)
    scrollableRef.current?.addEventListener('touchmove', resetTimer)
    scrollableRef.current?.addEventListener('scroll', resetTimer)

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
      document.removeEventListener('mousemove', resetTimer)
      document.removeEventListener('click', resetTimer)
      document.removeEventListener('keypress', resetTimer)
      scrollableRef.current?.removeEventListener('touchmove', resetTimer)
      scrollableRef.current?.removeEventListener('scroll', resetTimer)
    }
  }, [isMobile, idleModal?.open, scrollableRef?.current])

  return { idleModal, closeIdleModal }
}

const ArticleContent = ({ fallbackData }) => {
  const theme = useTheme()
  const isXLarge = useMediaQuery(theme.breakpoints.up('xl'))
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const isMedium = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const router = useRouter()

  useEffect(() => {
    setTimeout(
      () =>
        scrollableRef.current &&
        scrollableRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        }),
      0
    )
  }, [router.query.articleUrlId, router.query.classificationUrlId])

  const { data: article, isLoading: isArticleLoading } = useArticle(
    router.query.articleUrlId?.replace(/^DOC_/, 'doc_'),
    {
      options: fallbackData?.isForPreview ? { isForPreview: true } : {},
      swrOptions: fallbackData?.article && {
        fallbackData: [fallbackData?.article],
      },
    }
  )
  const { data: articleClassifications } = useArticleClassifications({
    isGetArticles: true,
  })

  const showArticleClassifications = articleClassifications?.filter(
    (item) => item.articles?.length > 0
  )

  const articleData = fallbackData?.article || article
  const { data: articleById } = useArticleById(articleData?._id)
  const { logoImage: articleVenderLogoImage } = getVenderWelcomeInfo(
    articleById?.vender
  )
  const webview = useSelector(selectWebview)

  useEffect(() => {
    if (webview?.name === 'lsApp') {
      try {
        enableNativeHeaderMainTabs(false)
        enableNativeHeaderNavBack(true)
        enableNativeHeaderSearch(false)
      } catch (err) {
        console.error('native notifier error: ', err.message)
      }
    }
  }, [webview])

  const { isLoading: isAuthLoading } = useLoginData()

  useEffect(() => {
    if (!isAuthLoading && !isArticleLoading) {
      setArticleViewed(article?._id)
    }
  }, [isAuthLoading, isArticleLoading])

  useGtmArticlePageView(article)

  const scrollableRef = useRef(null)
  const { idleModal, closeIdleModal } = useIdleModal(isMobile, scrollableRef)

  let breadcrumbs = [
    <CustomLink
      href={`/article-classification/${articleData?.articleClassification?.urlId}`}
      key="classification-page"
      $origin
    >
      <Typography variant="body2">
        <NowrapDiv>{articleData?.articleClassification?.name}</NowrapDiv>
      </Typography>
    </CustomLink>,
    <Typography variant="body2" color="textSecondary">
      <NowrapDiv key="article-content">{articleData?.title}</NowrapDiv>
    </Typography>,
  ]
  if (isLarge) {
    breadcrumbs = [
      <CustomLink href="/" key="homepage" $origin>
        <Typography variant="body2">
          <NowrapDiv>首頁</NowrapDiv>
        </Typography>
      </CustomLink>,
      <CustomLink href="/article" key="article-page" $origin>
        <Typography variant="body2">
          <NowrapDiv>找好文</NowrapDiv>
        </Typography>
      </CustomLink>,
    ].concat(breadcrumbs)
  }

  return isArticleLoading ? null : (
    <Layout stickyHeader={<Header />} fixContentHeight={true}>
      <ScrollableWrapper wrapperRef={scrollableRef}>
        <ArticleWrapper mt={2}>
          {isXLarge && (
            <Absolute>
              <Sticky>
                <Box>
                  <ShareButton
                    shareText={articleData.title}
                    isColumn={isXLarge}
                    utmSource={'livinglife_article'}
                  />
                </Box>
              </Sticky>
            </Absolute>
          )}
          <Box sx={{ width: '100%' }}>
            {webview?.name !== 'lsApp' && (
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                {breadcrumbs}
              </Breadcrumbs>
            )}
          </Box>
          <ArticleContentWrapper>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box mt={12} display="flex" justifyContent="start">
                  <Typography variant="h3" component="h1">
                    {articleData?.title}
                  </Typography>
                </Box>
                <Box mt={3} mb={10}>
                  <Grid
                    container
                    justifyContent="start"
                    alignItems="center"
                    spacing={3}
                  >
                    <Grid item xs={12} md={'auto'}>
                      <Box>
                        <OneLineGrid
                          container
                          spacing={4}
                          justifyContent="center"
                          $height={'60px'}
                        >
                          {articleData?.keywords?.map((keyword) => (
                            <Grid item key={keyword._id}>
                              <TagButton
                                href={`/article/tag/${keyword.urlId}`}
                                size="lg"
                              >
                                {keyword.name}
                              </TagButton>
                            </Grid>
                          ))}
                        </OneLineGrid>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={'auto'}>
                      <Box
                        display="flex"
                        justifyContent="center"
                        flexWrap="nowrap"
                      >
                        <IconInfos article={article} />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={8} pr={{ xs: 0, md: 15 }}>
                {!!articleData?.image && !article?.isImageInvisible && (
                  <MainImgBox>
                    <MainImg
                      src={articleData?.image?.url}
                      alt={articleData?.title}
                    />
                    <MainImageMaskWithLogo
                      venderName={
                        articleData.vender?.shortname || articleData?.byline
                      }
                      venderVatNo={articleById?.vender?.vatNo}
                      theme={theme}
                    />
                  </MainImgBox>
                )}

                {articleData?.docContents?.map((content, index) => (
                  <div key={`content-${index}`}>
                    {content?.title ? (
                      <Box mb={3}>
                        <Typography
                          variant="h4"
                          fontWeight="fontWeightBold"
                          component="h2"
                        >
                          {content?.title}
                        </Typography>
                      </Box>
                    ) : null}
                    <HTMLContent content={content?.content} type="article" />
                    <Box mb={14} />
                  </div>
                ))}
                <Grid
                  item
                  display="flex"
                  justifyContent="start"
                  alignItems="center"
                  xs={12}
                  pt={6}
                >
                  <SmallVenderIconImage
                    alt=""
                    src={
                      articleVenderLogoImage?.url ||
                      '/img/img_headshot_default.png'
                    }
                    onClick={
                      !!articleData?.vender?.introductionInfo?.isVisible
                        ? () => {
                            router.push(`/vender/${articleData?.vender?.urlId}`)
                          }
                        : null
                    }
                    style={{
                      cursor: !!articleData?.vender?.introductionInfo?.isVisible
                        ? 'pointer'
                        : 'default',
                    }}
                  />
                  <Typography ml={2} variant="subtitle2">
                    <Box fontWeight="fontWeightBold">
                      {!!articleData?.vender?.introductionInfo?.isVisible ? (
                        <Link
                          href={`/vender/${articleData.vender?.urlId}`}
                          $origin
                        >
                          {articleData.vender?.shortname}
                        </Link>
                      ) : (
                        articleData?.byline
                      )}
                    </Box>
                  </Typography>
                </Grid>
                <Box>
                  <Controls article={articleData} />
                </Box>
                {!isXLarge && (
                  <Box mt={12}>
                    <ShareButton
                      shareText={articleData.title}
                      utmSource={'livinglife_article'}
                    />
                  </Box>
                )}
                <Line />
                <Box
                  mt={25}
                  mb={15}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box sx={{ width: { xs: '98%', md: '90%' } }}>
                    <BannerCarousel
                      displayInfo="articlepage"
                      classification={article?.articleClassification?._id}
                      isMobile={isMobile}
                      bannerLocation="文章頁面"
                    />
                  </Box>
                </Box>
                <LatestArticle />
              </Grid>
              {/* 桌面版右側欄位 */}
              <Grid item xs={12} md={4}>
                {isMedium && (
                  <Box>
                    <VenderInfo
                      venderData={articleById?.vender}
                      articleByLine={articleData?.byline}
                    />
                  </Box>
                )}
                <Box mt={{ xs: 12, md: 0 }}>
                  <GuessLikesList article={articleData} />
                </Box>
                {!isMobile && (
                  <Box>
                    <Typography mb={6} mt={12} fontWeight="fontWeightBold">
                      文章分類
                    </Typography>
                    <Grid container spacing={2}>
                      {showArticleClassifications &&
                        showArticleClassifications.map((item, index) => {
                          return (
                            <ArticleClassifications
                              key={`item-${index}`}
                              articleClassificationItem={item}
                            />
                          )
                        })}
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>
          </ArticleContentWrapper>
        </ArticleWrapper>
        <BottomSpacing />
        {webview?.name !== 'lsApp' && <DynamicFooter />}
      </ScrollableWrapper>

      {isLarge && idleModal?.open && (
        <DynamicIdleModal
          open={idleModal?.open}
          onClose={closeIdleModal}
          articleClassificationId={article?.articleClassification?._id}
          articleId={article?._id}
        />
      )}
    </Layout>
  )
}

export default ArticleContent
